import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

import { useAppDispatch, useAppSelector } from "../redux";
import { getExperimentTimeseries } from "../redux/reducers/experimentsSlice";
import type { ExperimentDetail as ExperimentDetailType, ExperimentFunnel, SignificanceResult } from "../redux/reducers/experimentsSlice";
import { View, Text, Card, Badge, DropdownField, Spinner } from "./base";
import { Icon } from "./base/Icon";
import { COLORS } from "../utils/chartData";
import { useTheme } from "../contexts/ThemeContext";

// ---------------------------------------------------------------------------
// Time-range helpers (same as custom metrics page)
// ---------------------------------------------------------------------------

type TimeRange = "1h" | "6h" | "24h" | "7d" | "30d";

const TIME_RANGE_OPTIONS: { label: string; value: TimeRange }[] = [
  { label: "Last Hour", value: "1h" },
  { label: "Last 6 Hours", value: "6h" },
  { label: "Last 24 Hours", value: "24h" },
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 30 Days", value: "30d" },
];

const STEP_MAP: Record<TimeRange, string> = {
  "1h": "1m",
  "6h": "5m",
  "24h": "15m",
  "7d": "1h",
  "30d": "6h",
};

function getDateRange(range: TimeRange): { startDate: string; endDate: string } {
  const now = new Date();
  const end = now.toISOString();
  const ms: Record<TimeRange, number> = {
    "1h": 60 * 60 * 1000,
    "6h": 6 * 60 * 60 * 1000,
    "24h": 24 * 60 * 60 * 1000,
    "7d": 7 * 24 * 60 * 60 * 1000,
    "30d": 30 * 24 * 60 * 60 * 1000,
  };
  const start = new Date(now.getTime() - ms[range]).toISOString();
  return { startDate: start, endDate: end };
}

function formatTimestamp(timestamp: number, range: TimeRange): string {
  const date = new Date(timestamp * 1000);
  if (range === "1h" || range === "6h" || range === "24h") {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatValue(value: number): string {
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}K`;
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2);
}

function formatDate(date: string | Date) {
  const d = new Date(date);
  return d.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ---------------------------------------------------------------------------
// Significance badge helper
// ---------------------------------------------------------------------------

function significanceBadgeClass(confidence: number): string {
  if (confidence >= 99) return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
  if (confidence >= 95) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
  if (confidence >= 90) return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
  if (confidence >= 80) return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
  if (confidence >= 70) return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
  return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
}

// ---------------------------------------------------------------------------
// Chart tooltip
// ---------------------------------------------------------------------------

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <View className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">{label}</Text>
      {payload.map((entry: any, index: number) => (
        <View
          key={index}
          className="flex flex-row items-center gap-2"
        >
          <View
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <Text className="text-xs font-medium text-gray-900 dark:text-white">
            {entry.name}: {typeof entry.value === "number" ? formatValue(entry.value) : entry.value}
          </Text>
        </View>
      ))}
    </View>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusBadge = ({ completed }: { completed: boolean }) => (
  <View className="flex items-center">
    <Icon
      name={completed ? "check-circle" : "run"}
      size={12}
      className="mr-1"
    />
    <Badge variant={completed ? "success" : "warning"}>{completed ? "Completed" : "Running"}</Badge>
  </View>
);

const StopConditionsPanel = ({ stopConditions }: { stopConditions: ExperimentDetailType["stopConditions"] }) => {
  const hasMaxIterations = stopConditions.max_iterations > 0;
  const hasMaxConfidence = stopConditions.max_confidence > 0;
  if (!hasMaxIterations && !hasMaxConfidence) return null;

  return (
    <View className="mb-8">
      <View className="flex items-center mb-4">
        <Icon
          name="target"
          size={20}
          className="mr-2 text-purple-600 dark:text-purple-400"
        />
        <Text className="text-lg font-semibold">Stop Conditions</Text>
      </View>
      <View className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {hasMaxIterations && (
          <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
            <View className="p-4">
              <View className="flex items-center justify-between mb-3">
                <View className="flex items-center space-x-2">
                  <Icon
                    name="refresh"
                    size={16}
                    className="text-purple-600 dark:text-purple-400"
                  />
                  <Text className="font-medium">Iteration Limit</Text>
                </View>
                <Text className="text-sm text-purple-600 dark:text-purple-400 font-medium">Max: {stopConditions.max_iterations}</Text>
              </View>
              <View className="mb-3 p-2 bg-white dark:bg-gray-800 rounded">
                <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current Progress</Text>
                <View className="flex justify-between items-center">
                  <Text className="font-medium text-purple-700 dark:text-purple-300">{stopConditions.current_iterations}</Text>
                  <Text className="text-sm text-purple-600 dark:text-purple-400">of {stopConditions.max_iterations}</Text>
                </View>
              </View>
              {stopConditions.target_outcome && <div style={{ height: "35px" }} />}
              <View className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                <View className="flex justify-between text-sm">
                  <Text>Status:</Text>
                  <Text className="font-medium">{stopConditions.current_iterations >= stopConditions.max_iterations ? "Limit Reached" : "Active"}</Text>
                </View>
              </View>
            </View>
          </Card>
        )}

        {hasMaxConfidence && (
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <View className="p-4">
              <View className="flex items-center justify-between mb-3">
                <View className="flex items-center space-x-2">
                  <Icon
                    name="target"
                    size={16}
                    className="text-info-dark dark:text-info-light"
                  />
                  <Text className="font-medium">Confidence Target</Text>
                </View>
                <Text className="text-sm text-info-dark dark:text-info-light font-medium">{stopConditions.max_confidence}%</Text>
              </View>
              <View className="mb-3 p-2 bg-white dark:bg-gray-800 rounded">
                <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current Progress</Text>
                <View className="flex justify-between items-center">
                  <Text className="font-medium text-blue-700 dark:text-blue-300">{stopConditions.current_confidence.toFixed(1)}%</Text>
                  <Text className="text-sm text-info-dark dark:text-info-light">of {stopConditions.max_confidence}%</Text>
                </View>
              </View>
              {stopConditions.target_outcome && (
                <View className="mb-3 p-2 bg-white dark:bg-gray-800 rounded">
                  <View className="flex justify-between items-center">
                    <Text className="text-xs text-gray-500 dark:text-gray-400">Target Metric</Text>
                    <Text className="text-sm text-info-dark dark:text-info-light">{stopConditions.target_outcome}</Text>
                  </View>
                </View>
              )}
              <View className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                <View className="flex justify-between text-sm">
                  <Text>Status:</Text>
                  <Text className="font-medium">{stopConditions.current_confidence >= stopConditions.max_confidence ? "Target Reached" : "Active"}</Text>
                </View>
              </View>
            </View>
          </Card>
        )}
      </View>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Conversion Funnel section
// ---------------------------------------------------------------------------

const ConversionFunnelSection = ({ funnel }: { funnel: ExperimentFunnel }) => {
  if (!funnel?.funnel?.length) return null;

  const metrics = funnel.conversionMetrics;
  const entries = funnel.funnel;
  const baselineVariant = entries[0]?.variant;

  return (
    <Card>
      <View className="p-6">
        <View className="flex items-center mb-4">
          <Icon
            name="funnel"
            size={20}
            className="mr-2 text-purple-600 dark:text-purple-400"
          />
          <Text className="text-lg font-semibold">Conversion Funnel</Text>
        </View>

        {metrics.map((metric, mIdx) => (
          <View
            key={metric}
            className={mIdx > 0 ? "mt-6" : ""}
          >
            <Text className="font-medium mb-3 text-sm text-gray-600 dark:text-gray-400">{metric}</Text>

            <View className="space-y-4">
              {entries.map((entry, idx) => {
                const m = entry.metrics[metric];
                if (!m) return null;
                const maxSessions = Math.max(...entries.map((e) => e.sessions));
                const sessionsWidth = (entry.sessions / maxSessions) * 100;
                const conversionsWidth = m.conversions > 0 ? (m.conversions / maxSessions) * 100 : 0;
                const lift = entry.lift[metric];
                const isBaseline = entry.variant === baselineVariant;
                const color = COLORS[idx % COLORS.length];

                return (
                  <View
                    key={entry.variant}
                    className="space-y-2"
                  >
                    <View className="flex justify-between items-center">
                      <View className="flex items-center gap-2">
                        <View
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: color }}
                        />
                        <Text className="font-medium text-sm">{entry.variant}</Text>
                        {isBaseline && <Text className="text-xs text-gray-400 dark:text-gray-500">(baseline)</Text>}
                      </View>
                      <View className="flex items-center gap-3">
                        <Text className="text-sm font-bold">{(m.rate * 100).toFixed(1)}%</Text>
                        {!isBaseline && lift !== null && (
                          <Text
                            className={`text-xs font-medium ${lift > 0 ? "text-green-600 dark:text-green-400" : lift < 0 ? "text-red-600 dark:text-red-400" : "text-gray-500"}`}
                          >
                            {lift > 0 ? "+" : ""}
                            {(lift * 100).toFixed(1)}%
                          </Text>
                        )}
                      </View>
                    </View>

                    {/* Sessions bar */}
                    <View className="relative h-6 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                      <View
                        className="h-full rounded transition-all duration-500 opacity-30"
                        style={{ width: `${sessionsWidth}%`, backgroundColor: color }}
                      />
                      <View className="absolute inset-0 flex items-center px-2">
                        <Text className="text-xs text-gray-600 dark:text-gray-300">{entry.sessions} sessions</Text>
                      </View>
                    </View>

                    {/* Conversions bar */}
                    <View className="relative h-6 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                      <View
                        className="h-full rounded transition-all duration-500"
                        style={{ width: `${conversionsWidth}%`, backgroundColor: color }}
                      />
                      <View className="absolute inset-0 flex items-center px-2">
                        <Text className="text-xs text-gray-600 dark:text-gray-300">{m.conversions} conversions</Text>
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>

            {/* Funnel summary */}
            <View className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <View className="grid grid-cols-3 gap-4 text-center">
                <View>
                  <Text className="text-xs text-gray-500 dark:text-gray-400">Total Sessions</Text>
                  <Text className="text-lg font-bold">{funnel.totalSessions}</Text>
                </View>
                <View>
                  <Text className="text-xs text-gray-500 dark:text-gray-400">Best Rate</Text>
                  <Text className="text-lg font-bold text-green-600 dark:text-green-400">
                    {(Math.max(...entries.map((e) => e.metrics[metric]?.rate ?? 0)) * 100).toFixed(1)}%
                  </Text>
                </View>
                <View>
                  <Text className="text-xs text-gray-500 dark:text-gray-400">Variants</Text>
                  <Text className="text-lg font-bold">{entries.length}</Text>
                </View>
              </View>
            </View>
          </View>
        ))}
      </View>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Timeseries section
// ---------------------------------------------------------------------------

const TimeseriesSection = ({ experimentName, conversionMetrics }: { experimentName: string; conversionMetrics: string[] }) => {
  const { agentId } = useParams<{ agentId: string }>();
  const dispatch = useAppDispatch();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const { timeseries, timeseriesLoading, timeseriesError } = useAppSelector((state) => state.experiments);

  const [selectedMetric, setSelectedMetric] = useState<string>(conversionMetrics[0] || "");
  const [timeRange, setTimeRange] = useState<TimeRange>("7d");

  useEffect(() => {
    if (!selectedMetric || !agentId) return;
    const { startDate, endDate } = getDateRange(timeRange);
    dispatch(
      getExperimentTimeseries({
        agentId,
        experimentName,
        metricName: selectedMetric,
        startDate,
        endDate,
        step: STEP_MAP[timeRange],
      }),
    );
  }, [dispatch, agentId, experimentName, selectedMetric, timeRange]);

  const chartData = useMemo(() => {
    if (!timeseries?.result?.length) return { data: [], variantKeys: [] };

    const allTimestamps = new Set<number>();
    const series = timeseries.result;
    series.forEach((s) => s.values?.forEach(([ts]) => allTimestamps.add(ts)));

    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);

    // Extract variant names from metric labels
    const experimentLabel = `experiment_${experimentName}`;
    const variantKeys: string[] = [];
    series.forEach((s, idx) => {
      const variant = s.metric[experimentLabel] || `variant-${idx + 1}`;
      variantKeys.push(variant);
    });

    const data = sortedTimestamps.map((ts) => {
      const point: Record<string, any> = { time: formatTimestamp(ts, timeRange) };
      series.forEach((s, idx) => {
        const match = s.values?.find(([t]) => t === ts);
        point[variantKeys[idx]] = match ? parseFloat(match[1]) : null;
      });
      return point;
    });

    return { data, variantKeys };
  }, [timeseries, experimentName, timeRange]);

  if (!conversionMetrics.length) return null;

  const gridColor = isDark ? "#374151" : "#E5E7EB";
  const textColor = isDark ? "#9CA3AF" : "#6B7280";

  return (
    <Card>
      <View className="p-6">
        <View className="flex items-center justify-between mb-4">
          <View className="flex items-center">
            <Icon
              name="trending-up"
              size={20}
              className="mr-2 text-blue-600 dark:text-blue-400"
            />
            <Text className="text-lg font-semibold">Conversion Over Time</Text>
          </View>
          <View className="flex items-center gap-2">
            <DropdownField
              className="w-44"
              value={selectedMetric}
              onChange={setSelectedMetric}
              options={conversionMetrics.map((m) => ({ label: m, value: m }))}
            />
            <DropdownField
              className="w-36"
              value={timeRange}
              onChange={(v) => setTimeRange(v as TimeRange)}
              options={TIME_RANGE_OPTIONS}
            />
          </View>
        </View>

        {timeseriesLoading ? (
          <View className="flex items-center justify-center h-64">
            <Spinner size={24} />
          </View>
        ) : timeseriesError ? (
          <View className="flex items-center justify-center h-64">
            <Text className="text-xs text-red-500">{timeseriesError}</Text>
          </View>
        ) : !chartData.data.length ? (
          <View className="flex items-center justify-center h-64">
            <Text className="text-xs text-gray-400 dark:text-gray-500">No data for this time range</Text>
          </View>
        ) : (
          <ResponsiveContainer
            width="100%"
            height={300}
          >
            <LineChart
              data={chartData.data}
              margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={gridColor}
              />
              <XAxis
                dataKey="time"
                tick={{ fill: textColor, fontSize: 10 }}
                tickLine={{ stroke: gridColor }}
                axisLine={{ stroke: gridColor }}
              />
              <YAxis
                tick={{ fill: textColor, fontSize: 10 }}
                tickLine={{ stroke: gridColor }}
                axisLine={{ stroke: gridColor }}
                tickFormatter={formatValue}
                width={50}
              />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {chartData.variantKeys.map((key, idx) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={COLORS[idx % COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </View>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Significance section
// ---------------------------------------------------------------------------

const SignificanceSection = ({
  significance,
  conversionMetrics,
}: {
  significance: Record<string, Record<string, SignificanceResult>>;
  conversionMetrics: string[];
}) => {
  if (!Object.keys(significance).length) return null;

  return (
    <Card className="mb-8">
      <View className="p-6">
        <View className="flex items-center mb-4">
          <Icon
            name="bar-chart-2"
            size={20}
            className="mr-2 text-green-600 dark:text-green-400"
          />
          <Text className="text-lg font-semibold">Statistical Significance</Text>
        </View>

        {conversionMetrics.map((metric, mIdx) => {
          const metricSig = significance[metric];
          if (!metricSig) return null;

          return (
            <View
              key={metric}
              className={mIdx > 0 ? "mt-4" : ""}
            >
              <Text className="font-medium mb-3 text-sm text-gray-600 dark:text-gray-400">{metric}</Text>
              <View className="space-y-2">
                {Object.entries(metricSig).map(([pairKey, result]) => (
                  <View
                    key={pairKey}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                  >
                    <Text className="text-sm">{pairKey}</Text>
                    <View className="flex items-center gap-2">
                      <View className={`px-2 py-1 rounded text-xs ${significanceBadgeClass(result.confidence)}`}>
                        <Text className="text-xs">{result.significance}</Text>
                      </View>
                      <Text className="text-xs text-gray-500 dark:text-gray-400">p={result.pValue.toFixed(4)}</Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          );
        })}
      </View>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ExperimentDetail = () => {
  const { detail, funnel, funnelLoading } = useAppSelector((state) => state.experiments);

  if (!detail) return null;

  const variantDistributionData = detail.variants.map((v, idx) => ({
    name: v.name,
    value: v.sessionCount,
    percentage: detail.sessionCount > 0 ? ((v.sessionCount / detail.sessionCount) * 100).toFixed(1) : "0",
    color: COLORS[idx % COLORS.length],
  }));

  // Build conversion rate chart data per metric
  const conversionCharts = detail.conversionMetrics.map((metric) => ({
    metric,
    data: detail.variants.map((v) => ({
      variant: v.name,
      rate: v.conversions[metric] ? v.conversions[metric].rate * 100 : 0,
      count: v.conversions[metric]?.count ?? 0,
    })),
  }));

  return (
    <View className="bg-gray-50 dark:bg-gray-900 p-6">
      <View className="max-w-7xl mx-auto">
        {/* Header */}
        <View className="mb-8">
          <View className="flex justify-between items-start mb-2">
            <Text className="text-3xl font-bold">{detail.name}</Text>
            <StatusBadge completed={detail.completed} />
          </View>
          <View className="flex items-center gap-4 text-sm">
            <Text className="text-gray-500 dark:text-gray-400">Updated {formatDate(detail.lastUpdated)}</Text>
            <View className="flex items-center gap-4">
              <View className="flex items-center gap-1.5">
                <Icon
                  name="users"
                  size={14}
                  className="text-info-dark dark:text-info-light"
                />
                <Text className="font-medium">{detail.sessionCount}</Text>
                <Text className="text-gray-500 dark:text-gray-400">sessions</Text>
              </View>
              <View className="flex items-center gap-1.5">
                <Icon
                  name="variants"
                  size={14}
                  className="text-green-600 dark:text-green-400"
                />
                <Text className="font-medium">{detail.variants.length}</Text>
                <Text className="text-gray-500 dark:text-gray-400">variants</Text>
              </View>
              <View className="flex items-center gap-1.5">
                <Icon
                  name="activity"
                  size={14}
                  className="text-purple-600 dark:text-purple-400"
                />
                <Text className="font-medium">{detail.conversionMetrics.length}</Text>
                <Text className="text-gray-500 dark:text-gray-400">conversion metrics</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Stop Conditions */}
        <StopConditionsPanel stopConditions={detail.stopConditions} />

        {/* Variant Distribution + Conversion Rates */}
        <View className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {variantDistributionData.length > 0 && (
            <Card>
              <View className="p-6">
                <View className="flex items-center mb-4">
                  <Icon
                    name="pie-chart"
                    size={20}
                    className="mr-2 text-blue-600 dark:text-blue-400"
                  />
                  <Text className="text-lg font-semibold">Variant Distribution</Text>
                </View>
                <ResponsiveContainer
                  width="100%"
                  height={220}
                >
                  <PieChart>
                    <Pie
                      data={variantDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={70}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {variantDistributionData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </View>
            </Card>
          )}

          {conversionCharts.length > 0 && (
            <Card>
              <View className="p-6">
                <View className="flex items-center mb-4">
                  <Icon
                    name="bar-chart-2"
                    size={20}
                    className="mr-2 text-green-600 dark:text-green-400"
                  />
                  <Text className="text-lg font-semibold">Conversion Rates</Text>
                </View>
                {conversionCharts.map(({ metric, data }) => (
                  <View key={metric}>
                    <Text className="font-medium mb-3 text-sm text-gray-600 dark:text-gray-400">{metric}</Text>
                    <ResponsiveContainer
                      width="100%"
                      height={160}
                    >
                      <BarChart
                        data={data}
                        margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="variant"
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar
                          dataKey="rate"
                          fill={COLORS[0]}
                          radius={[4, 4, 0, 0]}
                        >
                          {data.map((_, idx) => (
                            <Cell
                              key={idx}
                              fill={COLORS[idx % COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>

                    <View className="grid grid-cols-2 gap-3 mt-3">
                      {data.map((d, idx) => (
                        <View
                          key={d.variant}
                          className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <View className="flex items-center gap-2 mb-1">
                            <View
                              className="w-2 h-2 rounded"
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            <Text className="text-sm font-medium">{d.variant}</Text>
                          </View>
                          <Text className="text-lg font-bold">{d.rate.toFixed(1)}%</Text>
                          <Text className="text-xs text-gray-500 dark:text-gray-400">{d.count} conversions</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                ))}
              </View>
            </Card>
          )}
        </View>

        {/* Conversion Funnel + Timeseries */}
        <View className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {funnelLoading ? (
            <View className="flex justify-center py-8">
              <Spinner size={24} />
            </View>
          ) : funnel ? (
            <ConversionFunnelSection funnel={funnel} />
          ) : null}
          <TimeseriesSection
            experimentName={detail.name}
            conversionMetrics={detail.conversionMetrics}
          />
        </View>

        {/* Statistical Significance */}
        <SignificanceSection
          significance={detail.significance}
          conversionMetrics={detail.conversionMetrics}
        />

        {/* Outcome Details */}
        {detail.variants.some((v) => Object.keys(v.outcomes).length > 0) && (
          <View className="mb-8">
            <View className="flex items-center mb-4">
              <Icon
                name="activity"
                size={20}
                className="mr-2 text-purple-600 dark:text-purple-400"
              />
              <Text className="text-lg font-semibold">Outcome Details</Text>
            </View>

            {detail.variants.map((variant, vIdx) => {
              const outcomeEntries = Object.entries(variant.outcomes);
              if (!outcomeEntries.length) return null;

              return (
                <Card
                  key={variant.name}
                  className="mb-4"
                >
                  <View className="p-6">
                    <View className="flex items-center gap-2 mb-4">
                      <View
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: COLORS[vIdx % COLORS.length] }}
                      />
                      <Text className="font-medium">{variant.name}</Text>
                      <Text className="text-sm text-gray-500 dark:text-gray-400">({variant.sessionCount} sessions)</Text>
                    </View>

                    <View className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {outcomeEntries.map(([outcomeName, outcome]) => {
                        const totalCounts = Object.values(outcome.counts).reduce((a, b) => a + b, 0);
                        return (
                          <View
                            key={outcomeName}
                            className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                          >
                            <View className="flex items-center justify-between mb-2">
                              <Text className="text-sm font-medium">{outcomeName}</Text>
                              <Badge variant="default">{outcome.type}</Badge>
                            </View>
                            <View className="space-y-1">
                              {Object.entries(outcome.counts)
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 5)
                                .map(([value, count]) => (
                                  <View
                                    key={value}
                                    className="flex justify-between text-xs"
                                  >
                                    <Text className="text-gray-600 dark:text-gray-300 truncate mr-2">{value}</Text>
                                    <Text className="font-medium whitespace-nowrap">
                                      {count} ({totalCounts > 0 ? ((count / totalCounts) * 100).toFixed(1) : 0}%)
                                    </Text>
                                  </View>
                                ))}
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                </Card>
              );
            })}
          </View>
        )}
      </View>
    </View>
  );
};
